import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Switch, TextField, useMediaQuery, useTheme
} from "@material-ui/core";

import { observer } from "mobx-react";
import { useStores } from "../../../../../hooks";
import { MenuFormType } from "../../../store/menu/forms";
import { changeCheckboxEventHandlerDecorator, changeEventHandlerDecorator, localize } from "../../../../../common/libs";
import { SnackbarType } from "../../../../../stores/common";
import {  Space } from "../../../../../common/components/libs";
import {Trans, useTranslation} from "react-i18next";
import {MenuCategory} from "../../../../../services/menu/models";
import MenuCategorySchedule from "./MenuCategorySchedule";
import LocalizedInput from "../../../../../common/components/translation/LocalizedInput";
import CookingTimeSelect from "./CookingTimeSelect";



const EditMenuCategoryForm = () => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const [name, setName] = useState("");
    const [validationNameErr, setValidationNameErr] = useState(false);
    const [sortOrder, setSortOrder] = useState(0);
    const [isInStopList, setIsInStopList] = useState(false);
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [isCookingTimeOpen, setIsCookingTimeOpen] = useState(false);

    const { menuStore, generalStore } = useStores();
    const { forms } = menuStore;
    const { open, current } = forms.forms[MenuFormType.createMenuCategory];
    const {i18n, t} = useTranslation(["general", "menu"]);


    useEffect(() => {

        let name = "";
        let sortOrder = 0;
        let isInStopList = false;
        let isScheduleOpen = false;
        if (current) {
            name = localize(current.data.name);
            sortOrder = current.data.sortOrder;
            isInStopList = current.data.isInStopList;
            isScheduleOpen = current.data.workingSchedule.isEnabled;
            menuStore.categoryWorkingSchedule = current.data.workingSchedule;
            menuStore.categoryCookingTime = current.data.categoryCookingTime;
        }
        setIsInStopList(isInStopList);
        setName(name);
        setSortOrder(sortOrder);
        setIsScheduleOpen(isScheduleOpen);
        if (current?.data.categoryCookingTime.fromTime) {
            setIsCookingTimeOpen(true)
        } else setIsCookingTimeOpen(false)
    }, [current]);


    const closeForm = () => {
        forms.closeForm(MenuFormType.createMenuCategory)
    };

    const setNameWrapper = (inputValue: string) => {
        if (inputValue !== "") {
            setName(inputValue);
            setValidationNameErr(false);
        } else {
            setValidationNameErr(true)
        }
    }
    const getHelperName = () => {
        let res = t("menu:nameErr");
        if (validationNameErr) return res;
    }

    let cookingTime;
    const getCookingTime = () => {
        if (isCookingTimeOpen) {
            return cookingTime = menuStore.categoryCookingTime
        } else return cookingTime = {fromTime: null, toTime: null}
    }

    const handleSubmit = async () => {
        if (current) {
            getCookingTime();
            await menuStore.updateCategory(current.id, {
                name: current.data.name,
                sortOrder,
                workingSchedule: {
                    ...menuStore.categoryWorkingSchedule,
                    isEnabled: isScheduleOpen
                },
                parentMenuCategoryId: null,
                isInStopList,
                categoryCookingTime: cookingTime,
            });
            menuStore.resetCategorySchedule();
            menuStore.resetCategoryCookingTime();
            generalStore.showSnackbar({
                title: t("menu:menuEditor.categoryUpdatedSuccessfully"),
                type: SnackbarType.success
            })
            closeForm();
        } else {
            if (name === "") {
                setValidationNameErr(true);
                const params: ({ type: SnackbarType, title: string, description?: string }) = {
                    title: t("menu:categoryNotCreated"),
                    description: t("menu:fulfillName"),
                    type: SnackbarType.error
                };
                generalStore.showSnackbar(params);
            } else {
                await menuStore.createCategory({
                    name, sortOrder,
                    workingSchedule: {
                        ...menuStore.categoryWorkingSchedule,
                        isEnabled: isScheduleOpen
                    },
                    parentMenuCategoryId: null,
                    isInStopList,
                    categoryCookingTime: menuStore.categoryCookingTime,
                });
                menuStore.resetCategorySchedule();
                menuStore.resetCategoryCookingTime();
                generalStore.showSnackbar({
                    title: t("menu:menuEditor.categorySuccessfullyCreated"),
                    type: SnackbarType.success
                })
                closeForm();
            }
        }
    };


    const openDeleteForm = (current?: MenuCategory) => {
        menuStore.forms.showPopupForm(MenuFormType.deleteMenuCategory, current);
    }


    let title = "";
    if (current) {
        title = t("general:editing");
    } else {
        title = t("menu:menuEditor.createCategory");
    }

    return (
        <Dialog
            open={open}
            onClose={closeForm}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth={"sm"}
            fullScreen={!isSm}
        >
            <DialogTitle id="form-dialog-title">
                {title}
            </DialogTitle>
            <DialogContent>
                {
                    (current && current.data && current.data.name) &&
                    <LocalizedInput
                        label = {t("general:name")}
                        value={current.data.name}
                        setNewValue ={menuStore.setCategoryName.bind(menuStore)}
                    />
                }
                {!current &&
                    <FormControl fullWidth margin={"normal"}>
                        <TextField
                            label={t("general:name")}
                            helperText={getHelperName()}
                            error={validationNameErr}
                            fullWidth={true}
                            value={name || ""}
                            onChange={changeEventHandlerDecorator(setNameWrapper)}
                        >
                        </TextField>
                    </FormControl>
                }
                <FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isInStopList}
                                onChange={changeCheckboxEventHandlerDecorator(setIsInStopList)}
                                value={!isInStopList}
                            />
                        }
                        label={t("menu:menuEditor.removeCategoryToStopList")}
                    />
                </FormControl>
                <FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isScheduleOpen}
                                onChange={changeCheckboxEventHandlerDecorator(setIsScheduleOpen)}
                                value={isScheduleOpen}
                            />
                        }
                        label={t("menu:categorySchedule")}
                    />
                </FormControl>

                {isScheduleOpen &&
                    <>
                        <Space size={5}/>
                        <MenuCategorySchedule/>
                        <Space size={5}/>
                    </>}
                <FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isCookingTimeOpen}
                                onChange={changeCheckboxEventHandlerDecorator(setIsCookingTimeOpen)}
                                value={isCookingTimeOpen}
                            />
                        }
                        label={t("menu:menuEditor.setCategoryCookingTime")}
                    />
                </FormControl>
                {isCookingTimeOpen &&
                    <>
                        <Space size={5}/>
                        <CookingTimeSelect item={menuStore.categoryCookingTime}
                                           updateValue={menuStore.setCategoryCookingTime.bind(menuStore)}/>
                    </>}
            </DialogContent>


            <DialogActions>
                {current && <Button
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openDeleteForm(current);
                    }}
                    style={{marginRight: 'auto'}}
                >
                    {t("general:delete")}
                </Button>}
                <Button onClick={closeForm} color="primary">
                    {t("general:cancel")}
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                >
                    {t("general:save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default observer(EditMenuCategoryForm);
